import {
  IApiPermissionItem,
  IApiRoleItem,
  IAuthorizationPermissionGroup,
} from '@/core/interfaces/ApiResponses';

export const LOCKED_ROLES = ['nguoi-dung'];

export const NGUOI_DUNG_ASSIGN_PERMISSION = 'data:nguoidung:sua';

export const ADMIN_ROLES = ['admin', 'superadmin'];

export const SUPERADMIN_ROLE = 'superadmin';

export function hasAdminRole(roleNames: string[]): boolean {
  return roleNames.some((name) => ADMIN_ROLES.includes(name));
}

export function hasSuperadminRole(roleNames: string[]): boolean {
  return roleNames.includes(SUPERADMIN_ROLE);
}

/**
 * Normalize a list API payload into a plain array.
 * Handles flat (`data: []`), paginated (`data: { data: [] }`),
 * and full ApiResponse wrappers (`{ status, data: ... }`).
 */
export function extractList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const record = payload as Record<string, unknown>;

  // Paginated: { data: [...], current_page, total, ... }
  if (Array.isArray(record.data)) {
    return record.data as T[];
  }

  // ApiResponse wrapper: { status, data: { data: [...] } | [...] }
  if ('status' in record && record.data !== undefined) {
    return extractList<T>(record.data);
  }

  return [];
}

/** Extract list items from axios ApiResponse body `{ status, data }`. */
export function extractPaginatedList<T>(
  apiBody: { data?: unknown } | undefined
): T[] {
  return extractList<T>(apiBody?.data);
}

const PERMISSION_GROUP_ORDER = ['master', 'data'];

export function groupPermissionsByPrefix(
  items: IApiPermissionItem[]
): IAuthorizationPermissionGroup[] {
  const groups = new Map<string, IApiPermissionItem[]>();

  items.forEach((item) => {
    const group = item.name.split(':')[0] || 'khac';
    if (!groups.has(group)) {
      groups.set(group, []);
    }
    groups.get(group)?.push(item);
  });

  const sortedEntries = Array.from(groups.entries()).sort(([a], [b]) => {
    const ai = PERMISSION_GROUP_ORDER.indexOf(a);
    const bi = PERMISSION_GROUP_ORDER.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  });

  return sortedEntries.map(([group, permissions]) => ({
    group,
    permissions: [...permissions].sort((a, b) => a.name.localeCompare(b.name)),
  }));
}

export function permissionGroupTitle(group: string, count: number): string {
  const labels: Record<string, string> = {
    master: 'Danh mục (master)',
    data: 'Nghiệp vụ (data)',
  };
  const label = labels[group] || group;
  return `${label} (${count})`;
}

export function filterAssignableRoles(
  roles: IApiRoleItem[],
  actorRoleNames: string[]
): IApiRoleItem[] {
  const actorIsSuperadmin = actorRoleNames.includes(SUPERADMIN_ROLE);

  return roles.filter((role) => {
    if (LOCKED_ROLES.includes(role.name)) return false;
    if (role.name === SUPERADMIN_ROLE && !actorIsSuperadmin) return false;
    return true;
  });
}

export function snapshotAuth(roles: string[], permissions: string[]): string {
  return JSON.stringify({
    roles: [...roles].sort(),
    permissions: [...permissions].sort(),
  });
}

export function isAuthDirty(
  currentSnapshot: string,
  initialSnapshot: string
): boolean {
  return currentSnapshot !== initialSnapshot;
}
